import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import { Loader } from "@tql/ui-components-react/kendo/indicators";
import type { RatingItemProps } from "@tql/ui-components-react/kendo/inputs";
import {
	Rating,
	RatingItem,
	TextArea,
} from "@tql/ui-components-react/kendo/inputs";
import { starIcon } from "@tql/ui-components-react/kendo/svg-icons";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { useAtom } from "jotai";
import { useEffect, useState } from "react";

import { feedbackOpen } from "~/shared/components/navbar/state/navbar-state.ts";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import { selectedCustomerAtom } from "~/shared/state/user-profile-state.ts";
import { useIsAuthenticated } from "~/shared/utils/use-is-authenticated.tsx";

import styles from "./feedback.module.css";
import { useSubmitFeedback } from "./hooks/use-submit-feedback.ts";

export const Feedback = () => {
	const [isFeedbackOpen, setIsFeedbackOpen] = useAtom(feedbackOpen);
	const [windowWidth, setWindowWidth] = useState(window.innerWidth);
	const [ratingValue, setRatingValue] = useState<number>(0);
	const [feedbackText, setFeedbackText] = useState<string>("");
	const { firstName, lastName, email } = useIsAuthenticated();
	const [selectedCustomer] = useAtom(selectedCustomerAtom);

	const { submitFeedbackAsync, isSubmitting, error, reset } =
		useSubmitFeedback();

	useEffect(() => {
		const handleResize = () => setWindowWidth(window.innerWidth);
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	if (!isFeedbackOpen) {
		return null;
	}

	const handleClose = () => {
		setIsFeedbackOpen(false);
		reset();
		setRatingValue(0);
		setFeedbackText("");
	};

	const isTinyMobile = windowWidth < 400;

	const CustomItem = (props: RatingItemProps) => {
		return (
			<RatingItem {...props}>
				<span>
					<SvgIcon
						icon={starIcon}
						size={isTinyMobile ? "xlarge" : "xxxlarge"}
					/>
				</span>
			</RatingItem>
		);
	};

	const handleSubmit = async () => {
		await submitFeedbackAsync({
			rating: ratingValue,
			feedback: feedbackText,
			// need the following
			firstName,
			lastName,
			email,
			companyName: selectedCustomer?.name ?? "",
			companyId: selectedCustomer?.id ?? 0,
		});
		if (!error) {
			handleClose();
		}
	};

	return (
		<TraxModal
			onClose={handleClose}
			title={"Feedback"}
			width={windowWidth < 600 ? "95%" : 600}
		>
			{error ? <p className={styles.error}>{error.message}</p> : null}
			{isSubmitting ? (
				<div className={styles.loaderContainer}>
					<Loader
						size={"large"}
						themeColor={"primary"}
						type={"infinite-spinner"}
					/>
				</div>
			) : (
				<>
					<div className={styles.container}>
						<div className={styles.question}>
							Overall, how well does TQL TRAX meet your needs?
							<span className={`required ${styles.ratingAsterisk}`}>*</span>
						</div>
						<div className={styles.ratingWrapper}>
							<div className={styles.ratingRelative}>
								<Rating
									item={CustomItem}
									max={5}
									min={1}
									onChange={(e) => setRatingValue(e.value ?? 0)}
									selection={"continues"}
									step={1}
									value={ratingValue}
								/>
							</div>
						</div>
					</div>
					<div>
						<div className={styles.label}>
							Tell us more: <span className={"required"}>*</span>
						</div>
						<TextArea
							onChange={(e) => setFeedbackText(e.target.value ?? "")}
							placeholder={"Please leave your feedback here"}
							rows={windowWidth < 600 ? 3 : 4}
							style={{ width: "100%" }}
						/>
					</div>
					<div className={styles.actions}>
						<Button onClick={handleClose}>Close</Button>
						<Button
							disabled={ratingValue === 0 || feedbackText.trim() === ""}
							onClick={() => {
								handleSubmit();
							}}
							variant={"primary"}
						>
							Submit
						</Button>
					</div>
				</>
			)}
		</TraxModal>
	);
};
